"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { ArrowLeft, Loader2, Lock } from "lucide-react";

const PACKAGES = [
  { id: "pack_500", credits: 500, bonus: 0, price: 4.99, label: "STARTER", tag: null },
  { id: "pack_1100", credits: 1000, bonus: 100, price: 9.99, label: "HUSTLE", tag: null },
  { id: "pack_3000", credits: 2500, bonus: 500, price: 24.99, label: "THE BAG", tag: "MOST WANTED" },
  { id: "pack_6500", credits: 5500, bonus: 1000, price: 49.99, label: "STACKS", tag: null },
  { id: "pack_15000", credits: 12000, bonus: 3000, price: 99.99, label: "WHALE", tag: "BLOXK BOSS" },
];

export default function SubscribePage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const i = setInterval(() => { setPulse(true); setTimeout(() => setPulse(false), 1200); }, 3500);
    return () => clearInterval(i);
  }, []);

  const checkout = async (id: string) => {
    setLoading(id);
    try {
      const res = await fetch("/api/credits/purchase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packageId: id }),
      });
      const data = await res.json();
      if (res.ok && data.url) window.location.href = data.url;
      else { alert(data.error || "Checkout failed"); setLoading(null); }
    } catch { alert("Something went wrong"); setLoading(null); }
  };

  return (
    <main className="min-h-screen bg-[#030303] text-white relative overflow-hidden">

      {/* ═══ FULL PAGE ATMOSPHERE ═══ */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Red glow orbs */}
        <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-red-900/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-[20%] right-0 w-[400px] h-[400px] bg-red-950/10 rounded-full blur-[100px]" />
        <div className={`absolute top-[30%] left-1/2 -translate-x-1/2 w-[300px] h-[300px] bg-red-600/5 rounded-full blur-[80px] transition-all duration-1000 ${pulse ? "scale-[1.8] opacity-100" : "scale-100 opacity-40"}`} />

        {/* Diagonal caution stripes */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 25px, rgba(220,38,38,0.4) 25px, rgba(220,38,38,0.4) 27px)",
        }} />

        {/* Scanlines */}
        <div className="absolute inset-0 opacity-[0.025]" style={{
          backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.04) 2px, rgba(255,255,255,0.04) 4px)",
        }} />

        {/* Noise */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }} />
      </div>

      {/* ═══ TOP CAUTION TAPE ═══ */}
      <div className="relative z-10 h-[3px]" style={{
        background: "repeating-linear-gradient(90deg, #dc2626 0px, #dc2626 10px, transparent 10px, transparent 20px)",
        opacity: 0.5,
      }} />

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8 md:py-14">

        <Link href="/" className="inline-flex items-center gap-2 text-neutral-600 hover:text-white text-xs uppercase tracking-widest mb-10 transition">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to the yard
        </Link>

        {/* ═══ HEADER — facility stencil ═══ */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-4 mb-5">
            <div className="h-px flex-1 max-w-[60px] bg-gradient-to-r from-transparent to-red-600/40" />
            <span className="text-[8px] font-black text-red-500/80 uppercase tracking-[0.5em] font-mono">Property of CellBloxk Barbie</span>
            <div className="h-px flex-1 max-w-[60px] bg-gradient-to-l from-transparent to-red-600/40" />
          </div>

          <h1 className="text-6xl md:text-8xl font-black uppercase text-white leading-[0.8] mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.06em" }}>
            COMMISSARY
          </h1>
          <h2 className="text-3xl md:text-4xl font-black uppercase leading-none" style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.04em" }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-400 to-pink-500">WINDOW OPEN</span>
          </h2>

          <p className="text-neutral-500 text-sm mt-6 max-w-lg mx-auto">
            Load your account. Drop commissary on inmates. Post bail on contraband. All sales final.
          </p>

          {/* Caution divider */}
          <div className="mt-8 h-[2px] max-w-[200px] mx-auto" style={{
            background: "repeating-linear-gradient(90deg, #dc2626 0px, #dc2626 8px, transparent 8px, transparent 16px)",
            opacity: 0.4,
          }} />
        </div>

        {/* ═══ PACKAGES — industrial cards ═══ */}
        <div className="space-y-3 mb-14">
          {PACKAGES.map((pkg) => {
            const isLoading = loading === pkg.id;
            const total = pkg.credits + pkg.bonus;
            const isHot = !!pkg.tag;

            return (
              <div
                key={pkg.id}
                className={`relative rounded-xl overflow-hidden transition-all duration-300 group ${
                  isHot ? "ring-1 ring-red-800/40" : ""
                }`}
              >
                {/* Card background */}
                <div className={`absolute inset-0 ${isHot ? "bg-red-950/25" : "bg-white/[0.02]"}`} />
                <div className="absolute inset-0 opacity-[0.02]" style={{
                  backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 15px, rgba(255,255,255,0.05) 15px, rgba(255,255,255,0.05) 16px)",
                }} />

                {/* Tag stripe */}
                {pkg.tag && (
                  <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-red-600 via-red-500 to-transparent" />
                )}

                {/* Red left accent */}
                <div className={`absolute left-0 top-0 bottom-0 w-[3px] transition-all ${
                  isHot ? "bg-red-600" : "bg-transparent group-hover:bg-red-800"
                }`} />

                <div className="relative flex items-center justify-between px-5 md:px-8 py-5 md:py-7">

                  {/* LEFT */}
                  <div className="flex items-center gap-4 md:gap-6">
                    {/* Credit amount — big stencil */}
                    <div className="min-w-[100px] md:min-w-[140px]">
                      <div className="text-2xl md:text-4xl font-black text-white font-mono tracking-tight group-hover:text-red-400 transition-colors" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {total.toLocaleString()}
                      </div>
                      <div className="text-[8px] text-neutral-600 uppercase tracking-[0.3em] font-mono mt-0.5">commissary</div>
                    </div>

                    {/* Label + bonus */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-black text-neutral-300 uppercase tracking-widest">{pkg.label}</span>
                        {pkg.tag && (
                          <span className="text-[7px] font-black text-red-400 uppercase tracking-[0.15em] border border-red-800/50 bg-red-900/20 px-1.5 py-0.5 rounded">{pkg.tag}</span>
                        )}
                      </div>
                      {pkg.bonus > 0 && (
                        <div className="text-[10px] font-bold text-green-500 mt-1">
                          +{pkg.bonus.toLocaleString()} bonus included
                        </div>
                      )}
                    </div>
                  </div>

                  {/* RIGHT — price + action */}
                  <button
                    onClick={() => checkout(pkg.id)}
                    disabled={loading !== null}
                    className={`relative flex items-center gap-3 px-5 md:px-7 py-3 md:py-3.5 rounded-lg font-black uppercase text-sm tracking-wider transition-all disabled:opacity-50 shrink-0 ${
                      isHot
                        ? "bg-red-600 hover:bg-red-500 text-white shadow-[0_0_25px_rgba(220,38,38,0.3)] hover:shadow-[0_0_35px_rgba(220,38,38,0.5)]"
                        : "bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/[0.06] hover:border-red-800/30"
                    }`}
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <span className="text-base md:text-lg font-mono">${pkg.price.toFixed(2)}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* ═══ TRUST BAR ═══ */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-4 md:gap-6 text-[9px] text-neutral-500 uppercase tracking-[0.2em] font-mono">
            <div className="flex items-center gap-1.5">
              <Lock className="w-3 h-3 text-neutral-600" />
              <span>Stripe secured</span>
            </div>
            <span className="w-1 h-1 bg-neutral-800 rounded-full" />
            <span>Instant load</span>
            <span className="w-1 h-1 bg-neutral-800 rounded-full" />
            <span>80% to inmates</span>
          </div>
        </div>

        {/* ═══ BOTTOM CAUTION + FINE PRINT ═══ */}
        <div className="h-[2px] mx-auto max-w-xs mb-6" style={{
          background: "repeating-linear-gradient(90deg, #dc2626 0px, #dc2626 8px, transparent 8px, transparent 16px)",
          opacity: 0.3,
        }} />
        <p className="text-center text-[8px] text-neutral-700 font-mono uppercase tracking-[0.3em]">
          All commissary is non-refundable -- SupaMane Technologies LLC
        </p>
      </div>

      {/* ═══ BOTTOM CAUTION TAPE ═══ */}
      <div className="relative z-10 h-[3px] mt-auto" style={{
        background: "repeating-linear-gradient(90deg, #dc2626 0px, #dc2626 10px, transparent 10px, transparent 20px)",
        opacity: 0.5,
      }} />
    </main>
  );
}
